import { Avatar, Box, Checkbox, TableCell, TableRow } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import {
  ApprovalWidgetCategory,
  getFormattedDate,
} from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/data';
import { ApprovalWidgetData } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/types';
import { getInitials } from '~/services/Employee';
import { mergeSxProps } from '~/utils/helpers/sxProps';

import { useSxStyles } from './useStyles';

type ApprovalRowProps = {
  data: ApprovalWidgetData;
  actionDetails: string;
  category: ApprovalWidgetCategory;
  showEndDate?: boolean;
  categoryThirdValue: (widgetData: ApprovalWidgetData) => string;
  rowMode: ApprovalRowMode;
  secondCellName: string;
  thirdCellName: string;
  selection: ApprovalWidgetData[];
  onSelection: (selection: ApprovalWidgetData[]) => void;
  isSubmitting: boolean;
};

export enum ApprovalRowMode {
  Table = 'table',
  SemiItem = 'semi-item',
  Item = 'item',
}

export const ApprovalRow = (props: ApprovalRowProps) => {
  const {
    data,
    actionDetails,
    category,
    showEndDate,
    categoryThirdValue,
    rowMode,
    secondCellName,
    thirdCellName,
    selection,
    onSelection,
    isSubmitting,
  } = props;
  const sxStyles = useSxStyles();

  const handleIndividualCheck = useCallback(
    (checkValue: boolean, requestData: ApprovalWidgetData) => {
      const filterUnselected = (
        oldSelection: ApprovalWidgetData[],
        unselected: ApprovalWidgetData,
      ) => {
        switch (category) {
          case ApprovalWidgetCategory.Absences:
          case ApprovalWidgetCategory.Allowance:
          case ApprovalWidgetCategory.Benefit:
          case ApprovalWidgetCategory.Incentive:
          case ApprovalWidgetCategory.Salary:
            return oldSelection.filter(
              (selected) => selected.id !== unselected.id,
            );
          case ApprovalWidgetCategory.Training:
            return oldSelection.filter(
              (selected) => selected.uid !== unselected.uid,
            );
          default:
            return [];
        }
      };

      onSelection(
        checkValue
          ? [...selection, requestData]
          : filterUnselected(selection, requestData),
      );
    },
    [category, selection, onSelection],
  );

  const isSelected = useMemo((): boolean => {
    switch (category) {
      case ApprovalWidgetCategory.Absences:
      case ApprovalWidgetCategory.Allowance:
      case ApprovalWidgetCategory.Benefit:
      case ApprovalWidgetCategory.Incentive:
      case ApprovalWidgetCategory.Salary:
        return !!selection.find((item) => item.id === data.id);
      case ApprovalWidgetCategory.Training:
        return !!selection.find((item) => item.uid === data.uid);
      default:
        return false;
    }
  }, [category, selection, data]);

  const tableMode = rowMode === ApprovalRowMode.Table;

  return (
    <TableRow
      key={data.employee.id}
      sx={mergeSxProps(
        sxStyles.tableRow,
        rowMode === ApprovalRowMode.SemiItem ? sxStyles.semiItemRow : undefined,
        rowMode === ApprovalRowMode.Item ? sxStyles.itemRow : undefined,
        isSelected ? sxStyles.selectedTableRow : undefined,
      )}
    >
      <TableCell sx={{ ...sxStyles.tableCellRoot, ...sxStyles.smallCell }}>
        <Checkbox
          color="primary"
          onChange={(e) => handleIndividualCheck(e.target.checked, data)}
          checked={isSelected}
          disabled={isSubmitting}
        />
      </TableCell>
      <TableCell
        sx={{ ...sxStyles.tableCellRoot, ...sxStyles.textCell }}
        className={'textCell'}
      >
        <Avatar
          sx={sxStyles.avatar}
          alt={getInitials(data.employee.firstName, data.employee.lastName)}
          src={data.employee.profilePic}
        />
        <Box className={'textCellContent'}>
          <Box component="span">
            {data.employee.firstName + ' ' + data.employee.lastName}
          </Box>
          <TooltippedTruncatedText>{actionDetails}</TooltippedTruncatedText>
        </Box>
      </TableCell>
      <TableCell
        sx={{ ...sxStyles.tableCellRoot, ...sxStyles.dateCell }}
        className={'dateCell'}
      >
        {!tableMode && <Box sx={sxStyles.cellLabel}>{secondCellName}</Box>}
        {`${getFormattedDate(data.startDate)}${
          showEndDate && data.endDate
            ? ` - ${getFormattedDate(data.endDate)}`
            : ''
        }`}
      </TableCell>
      <TableCell
        sx={{ ...sxStyles.tableCellRoot, ...sxStyles.daysCell }}
        className={'daysCell'}
      >
        {!tableMode && <Box sx={sxStyles.cellLabel}>{thirdCellName}</Box>}
        {categoryThirdValue(data)}
      </TableCell>
    </TableRow>
  );
};
